/**
 * Гутлын төлөв бүрийн карт — дарахад яг тэр төлөвийн гутлын жагсаалт
 * («Бүх гутал» → `?status=`) нээгдэнэ (захиалагчийн цэгцлэл №3).
 *
 * Өмнө нь «Төлөв бүрээр» тууз байж, тоо нь харагдах ч АЛЬ гутал гэдгийг
 * олох замгүй байв.
 *
 * ⚠️ Дараалал нь гутлын АМЬДРАЛЫН мөчлөг — тоогоор эрэмбэлэхгүй: картууд
 *    байраа солих болвол ажилтан «Зарагдсан»-аа өдөр бүр хайна.
 * ⚠️ Өнгө нь төлөвийн БҮЛЭГ: нөөцөд (оюу) · гарсан (саарал) · асуудалтай
 *    (ягаан). Карт бүрийг өөр өнгөөр будахгүй.
 */

import { formatQty } from '@boots/domain';
import { Link } from 'react-router-dom';
import { STATUS_LABEL, type ItemStatus } from '../../lib/types';
import { Meter, type MeterTone } from '../meter';

const ORDER: readonly ItemStatus[] = [
  'COLLECTED', 'AVAILABLE', 'RETURNED', 'RESERVED', 'SOLD', 'DEFECTIVE', 'WRITTEN_OFF',
];

/** «Энэ юу вэ?» — карт бүрийн доор нэг мөр. */
const MEANING: Record<ItemStatus, string> = {
  COLLECTED: 'Бүртгэгдсэн ч зарагдах байршилд хараахан очоогүй',
  AVAILABLE: 'Нөөцөд — агуулах эсвэл салбарт, зарагдах боломжтой',
  RETURNED: 'Худалдан авагч буцаасан, дахин зарагдах боломжтой',
  RESERVED: 'Түр захиалгад',
  SOLD: 'Худалдан авагчид очсон — баталгаа нь хүчинтэй байж болно',
  DEFECTIVE: 'Гэмтэлтэй — хорио цээр, засвар эсвэл нийлүүлэгч рүү',
  WRITTEN_OFF: 'Актлагдсан — нөөцөөс бүрмөсөн хасагдсан',
};

const TONE: Record<ItemStatus, MeterTone> = {
  COLLECTED: 'accent-soft',
  AVAILABLE: 'accent',
  RETURNED: 'accent',
  RESERVED: 'accent-soft',
  SOLD: 'neutral',
  DEFECTIVE: 'alert',
  WRITTEN_OFF: 'alert',
};

export function StatusTiles({ counts }: { counts: Partial<Record<ItemStatus, number>> }) {
  const total = ORDER.reduce((n, s) => n + (counts[s] ?? 0), 0);
  // Түр захиалга хийгддэггүй (CLAUDE.md «Хариу хүлээж буй» №5) — 0 бол нуух
  const shown = ORDER.filter((s) => s !== 'RESERVED' || (counts[s] ?? 0) > 0);

  return (
    <ul className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-4">
      {shown.map((s) => {
        const n = counts[s] ?? 0;
        const pct = total > 0 ? (n / total) * 100 : 0;
        return (
          <li key={s}>
            <Link
              to={`/items?status=${s}`}
              className="flex h-full flex-col gap-1.5 rounded-well border border-hair bg-panel px-3.5 py-3
                transition hover:border-accent-300 hover:bg-row-hover focus-visible:border-accent"
            >
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-meta text-ink-2">{STATUS_LABEL[s]}</span>
                <span className="num text-micro text-ink-3">{Math.round(pct)}%</span>
              </span>
              <span className="num text-[22px] leading-tight font-semibold">
                {formatQty(n)}
                <span className="ml-1 text-meta font-normal text-ink-2">ш</span>
              </span>
              <Meter value={pct} tone={TONE[s]} size="sm" />
              <span className="text-micro leading-snug text-pretty text-ink-3">{MEANING[s]}</span>
              <span className="mt-auto pt-0.5 text-micro text-accent-700">Жагсаалт харах →</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
